import { NavLink, Outlet, Link } from "react-router-dom";
import { Dna, FlaskConical, BookOpenText, BookText, ShieldCheck, Sparkles, Bird } from "lucide-react";
import { usePro } from "@/lib/pro";

const NAV = [
  { to: "/", label: "Lab Home", icon: Dna, end: true, testId: "nav-home" },
  { to: "/calculator", label: "Breeding Calculator", icon: FlaskConical, testId: "nav-calculator" },
  { to: "/flock", label: "My Flock", icon: Bird, testId: "nav-flock" },
  { to: "/encyclopedia", label: "Mutation Index", icon: BookOpenText, testId: "nav-encyclopedia" },
  { to: "/genetics-101", label: "Genetics 101", icon: BookText, testId: "nav-genetics101" },
];

export default function Layout() {
  const { isPro } = usePro();
  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <header
        data-testid="site-header"
        className="border-b border-border bg-card sticky top-0 z-30 backdrop-blur"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <NavLink
            to="/"
            className="flex items-center gap-3"
            data-testid="logo-link"
          >
            <div className="w-8 h-8 grid place-items-center bg-primary text-primary-foreground rounded-sm">
              <Dna size={18} strokeWidth={2.4} />
            </div>
            <div className="leading-tight">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                Aviary Lab
              </div>
              <div className="font-black tracking-tight text-lg">
                Budgie Genetics
              </div>
            </div>
          </NavLink>

          <nav className="hidden md:flex items-center gap-1">
            {NAV.map(({ to, label, icon: Icon, end, testId }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={testId}
                className={({ isActive }) =>
                  [
                    "flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-medium transition-colors",
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground hover:bg-secondary",
                  ].join(" ")
                }
              >
                <Icon size={15} />
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
            {isPro ? (
              <span
                data-testid="pro-badge"
                className="inline-flex items-center gap-1.5 bg-primary/10 text-primary px-2 py-1 rounded-sm"
              >
                <ShieldCheck size={12} /> Pro
              </span>
            ) : (
              <Link
                to="/pro"
                data-testid="nav-pro-cta"
                className="inline-flex items-center gap-1.5 bg-foreground text-background px-2.5 py-1 rounded-sm hover:bg-primary transition-colors"
              >
                <Sparkles size={12} /> Get Pro
              </Link>
            )}
            <span>v1.1 · 2026</span>
          </div>
        </div>

        {/* Mobile nav */}
        <div className="md:hidden border-t border-border overflow-x-auto">
          <div className="flex gap-1 px-4 py-2">
            {NAV.map(({ to, label, end, testId }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={`${testId}-mobile`}
                className={({ isActive }) =>
                  [
                    "px-3 py-1.5 rounded-sm text-xs font-medium whitespace-nowrap transition-colors",
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground hover:bg-secondary",
                  ].join(" ")
                }
              >
                {label}
              </NavLink>
            ))}
          </div>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer
        data-testid="site-footer"
        className="border-t border-border bg-card mt-16"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 md:grid-cols-3 gap-8 text-sm">
          <div>
            <div className="overline mb-2">Lab Notes</div>
            <p className="text-muted-foreground leading-relaxed">
              An open scientific tool for budgerigar breeders. Punnett-square
              accurate predictions across worldwide mutations.
            </p>
          </div>
          <div>
            <div className="overline mb-2">Genetic Loci Modelled</div>
            <p className="font-mono text-xs text-muted-foreground leading-relaxed">
              Base · Dark Factor · Violet · Grey · Spangle · Dom. Pied · Rec.
              Pied · Crest · Yellowface · Dilution · Ino · Cinnamon · Opaline
            </p>
          </div>
          <div>
            <div className="overline mb-2">References</div>
            <p className="text-muted-foreground leading-relaxed">
              World Budgerigar Organisation (WBO) colour standards, BSSA, and
              published avian-genetics literature.
            </p>
          </div>
        </div>
        <div className="border-t border-border">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex flex-col md:flex-row justify-between gap-2 text-xs text-muted-foreground font-mono uppercase tracking-widest">
            <span>© 2026 Aviary Lab</span>
            <span>// chromosome ZZ ♂  ZW ♀</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
